import { useEffect, useState } from "react";
import { PlugZap } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { INTEGRATION_MAPPINGS } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";

/**
 * Loads the eMedHub employee↔doctor mappings once and returns a Map keyed by the
 * HRMS employee id -> { doctorName, status, externalDoctorId }. Used to flag which
 * employees are integrated. Fails quietly (e.g. for non-admins) -> empty map.
 */
export const useIntegrationMappings = () => {
    const { fetch } = useApiMethods();
    const [map, setMap] = useState(() => new Map());

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const res = await fetch(INTEGRATION_MAPPINGS, {});
                if (active && res?.status === "success" && Array.isArray(res.data)) {
                    const next = new Map();
                    res.data.forEach((row) => {
                        if (row.employee_id != null) {
                            // Key by string so a number/string id mismatch can't miss.
                            next.set(String(row.employee_id), {
                                doctorName: row.external_name,
                                status: row.status,
                                externalDoctorId: row.external_doctor_id,
                            });
                        }
                    });
                    setMap(next);
                }
            } catch (e) {
                /* integration not available / not an admin — show no badges */
            }
        })();
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return map;
};

/** Small "integrated with eMedHub" chip. Renders nothing when not mapped. */
export const IntegratedBadge = ({ mapping, withLabel = true, className }) => {
    if (!mapping) return null;
    const title = `Integrated with eMedHub${mapping.doctorName ? ` · ${mapping.doctorName}` : ""}`;
    return (
        <span
            title={title}
            className={cn(
                "inline-flex items-center gap-1 rounded-full border border-sidebar/30 bg-sidebar/10 text-sidebar-primary",
                withLabel ? "px-1.5 py-0.5 text-[10px] font-medium" : "p-1",
                className
            )}
        >
            <PlugZap size={12} />
            {withLabel && "Integrated"}
        </span>
    );
};
